import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Upload, Trash2, Image as ImageIcon, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { galeriaAPI } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

export default function AdminGaleria() {
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({ titulo: '', descricao: '' });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { toast } = useToast();

  const fetchFotos = async () => {
    try {
      const res = await galeriaAPI.listar();
      setFotos(res.data);
    } catch (error) {
      console.error('Erro ao carregar fotos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFotos();
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles(acceptedFiles);
    if (acceptedFiles.length > 0) {
      setModalOpen(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    multiple: true
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    try {
      for (const file of selectedFiles) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        if (formData.titulo) formDataUpload.append('titulo', formData.titulo);
        if (formData.descricao) formDataUpload.append('descricao', formData.descricao);
        
        await galeriaAPI.upload(formDataUpload);
      }
      
      toast({ title: `${selectedFiles.length} foto(s) enviada(s) com sucesso!` });
      setModalOpen(false);
      setSelectedFiles([]);
      setFormData({ titulo: '', descricao: '' });
      fetchFotos();
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      toast({ title: "Erro ao enviar fotos", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta foto?')) return;
    try {
      await galeriaAPI.deletar(id);
      toast({ title: "Foto excluída com sucesso!" });
      fetchFotos();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const getImageUrl = (foto) => {
    if (foto.url.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${foto.url}`;
    }
    return foto.url;
  };

  return (
    <div data-testid="admin-galeria">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Galeria</h1>
        <p className="text-slate-600 mt-1">Gerencie as fotos do time</p>
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        data-testid="dropzone"
        className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors mb-8 ${
          isDragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
        {isDragActive ? (
          <p className="text-blue-600 font-medium">Solte as imagens aqui...</p>
        ) : (
          <>
            <p className="text-slate-600 font-medium">Arraste e solte imagens aqui</p>
            <p className="text-slate-400 text-sm mt-1">ou clique para selecionar</p>
          </>
        )}
      </div>

      {/* Grid de Fotos */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : fotos.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <ImageIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Nenhuma foto na galeria</h3>
          <p className="text-slate-500">Arraste imagens para o campo acima para começar.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {fotos.map((foto, index) => (
            <motion.div
              key={foto.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="group relative bg-white rounded-lg overflow-hidden shadow-sm"
              data-testid={`galeria-item-${foto.id}`}
            >
              <div className="aspect-square">
                <img
                  src={getImageUrl(foto)}
                  alt={foto.titulo || 'Foto'}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => handleDelete(foto.id)}
                  data-testid={`delete-foto-${foto.id}`}
                  className="bg-red-500 text-white p-3 rounded-full hover:bg-red-600 transition-colors"
                >
                  <Trash2 size={20} />
                </button>
              </div>

              {/* Info */}
              {foto.titulo && (
                <div className="p-3">
                  <p className="font-medium text-slate-900 text-sm truncate">{foto.titulo}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal de Upload */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="upload-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-lg"
          >
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="font-heading text-xl font-bold">Upload de Fotos</h2>
              <button onClick={() => { setModalOpen(false); setSelectedFiles([]); }} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpload} className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">
                  <strong>{selectedFiles.length}</strong> arquivo(s) selecionado(s)
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedFiles.slice(0, 5).map((file, i) => (
                    <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {file.name}
                    </span>
                  ))}
                  {selectedFiles.length > 5 && (
                    <span className="text-xs text-slate-500">+{selectedFiles.length - 5} mais</span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Título (opcional)</label>
                <input
                  type="text"
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  data-testid="modal-input-titulo"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="Ex: Treino 2024"
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Descrição (opcional)</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  rows={2}
                  data-testid="modal-input-descricao"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 resize-none"
                  placeholder="Descrição das fotos..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setModalOpen(false); setSelectedFiles([]); }}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg font-bold text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  data-testid="modal-submit-btn"
                  className="flex-1 px-6 py-3 bg-blue-700 text-white rounded-lg font-bold hover:bg-blue-800 disabled:opacity-50"
                >
                  {uploading ? 'Enviando...' : 'Enviar Fotos'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
